/**
 * The capability matrix — the authoritative gating table for
 * 028-membership-state-gating.
 *
 * Contract: `specs/028-membership-state-gating/contracts/capability-gating.md`.
 * Research Decision 3 (the matrix is data, one source of truth read by both
 * the UI greying and the route guard, so the nav, routes, invoice, and
 * Messages surfaces cannot drift).
 *
 * `capabilityFor(status, capability)` is a total function (contract C1): every
 * (status, capability) pair has a verdict. Surfaces call `useCapabilityAllowed`
 * (which reads the trusted classification and this matrix); no surface
 * branches on `MembershipStatus` directly to make its own decision.
 */

import type { MembershipStatus } from './membershipStatus.js';

/**
 * The gated member-surface actions (the rows of the matrix). `other_actions`
 * is the catch-all for everything not individually carved out.
 */
export type Capability =
  | 'notice_shown'
  | 'join_link'
  | 'view_latest_invoice'
  | 'pay_invoice_in_service'
  | 'open_messages'
  | 'jasenpalvelu_read'
  | 'jasenpalvelu_send'
  | 'lakikaverille_read_old'
  | 'lakikaverille_send_new'
  | 'other_actions';

/**
 * A capability's verdict for a state. `allowed` = active; `greyed` = visible
 * but disabled and refused by the guard; `not_applicable` = the affordance is
 * not offered for that state (the notice/join for a valid member).
 */
export type CapabilityVerdict = 'allowed' | 'greyed' | 'not_applicable';

/**
 * The matrix as data. Keyed by status, then capability. Total over both axes.
 * BINARY under D-202 (042): only `none` vs `valid` — there is no `lapsed`
 * carve-out column any more. A `valid` member has every functional capability;
 * `none` (never-member OR ended membership, which is refused a session) gets
 * only the join surface (`notice_shown` + `join_link`), everything else greyed.
 */
const MATRIX: Readonly<Record<MembershipStatus, Readonly<Record<Capability, CapabilityVerdict>>>> =
  {
    none: {
      notice_shown: 'allowed',
      join_link: 'allowed',
      view_latest_invoice: 'greyed',
      pay_invoice_in_service: 'greyed',
      open_messages: 'greyed',
      jasenpalvelu_read: 'greyed',
      jasenpalvelu_send: 'greyed',
      lakikaverille_read_old: 'greyed',
      lakikaverille_send_new: 'greyed',
      other_actions: 'greyed',
    },
    valid: {
      notice_shown: 'not_applicable',
      join_link: 'not_applicable',
      view_latest_invoice: 'allowed',
      pay_invoice_in_service: 'allowed',
      open_messages: 'allowed',
      jasenpalvelu_read: 'allowed',
      jasenpalvelu_send: 'allowed',
      lakikaverille_read_old: 'allowed',
      lakikaverille_send_new: 'allowed',
      other_actions: 'allowed',
    },
  };

/**
 * Returns the verdict for a capability under a membership state. Total
 * function (contract C1): every pair resolves to a verdict.
 */
export function capabilityFor(status: MembershipStatus, capability: Capability): CapabilityVerdict {
  return MATRIX[status][capability];
}

/**
 * Convenience predicate: is the capability active for the state? `true` only
 * for `allowed`; both `greyed` and `not_applicable` are not active. Callers
 * gate affordances and routes on this.
 */
export function isCapabilityAllowed(status: MembershipStatus, capability: Capability): boolean {
  return capabilityFor(status, capability) === 'allowed';
}
